import {
  useBoolean,
  useMemoizedFn,
  useRequest,
  useSessionStorageState,
} from 'ahooks'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FlowExecuteEnv, FlowStatus, getFlowExecuteLog } from '@/apis/flow'
import { API_CODE_CODE } from '@/constants/request'
import {
  StreamMessageType,
  useStreamingExecuteFlow,
} from '@/hooks/useStreamingExecuteFlow'
import { useFlowLogStore, useNodeStore } from '@/store'
import type { FlowOutput } from '@/features/logger/types'
import { transformDataToValidLogs } from '@/features/logger/utils'
import { FLOW_DEBUG_MAP, FLOW_ERROR_LOG } from '@/features/logger/constants'
import { LIMITED_ERROR_COMBO_NOT_ENOUGH } from '@/constants/pay'
import { useFlowDraftValidate } from './useFlowDraftValidate'

interface ExecuteResult {
  status?: FlowStatus
  taskId?: string
  result?: string | null
  errorMsg?: string | null
  stepName?: string | null
}

interface RunDebugOptions {
  versionId?: string
}

export function useFlowDraftRunDebug(options: RunDebugOptions) {
  const { id } = useParams()
  const { versionId } = options

  const [flowErrorLog] = useSessionStorageState<any>(FLOW_ERROR_LOG)
  const [flowDebugMap = {}, setFlowDebugMap] =
    useSessionStorageState<any>(FLOW_DEBUG_MAP)

  const [open, setOpen] = useState(
    flowErrorLog?.run_type === 'STEP_RUN'
      ? false
      : !!flowDebugMap[id!] || false,
  )
  const [logModalOpen, setLogModalOpen] = useState(false)
  const [tabKey, setTabKey] = useState('input')
  const inputDataRef = useRef<Record<string, any>>({})
  const [executeResult, setExecuteResult] = useState<ExecuteResult>({})
  const [executeLogs, setExecuteLogs] = useState<FlowOutput[]>([])
  const messageType = useRef(StreamMessageType.PIECEWISE)
  const [
    streamLoading,
    { setTrue: startStreamLoading, setFalse: stopStreamLoading },
  ] = useBoolean(false)
  const { validate } = useFlowDraftValidate()
  const { deactivateNode, activatedNodeId } = useNodeStore()
  const { setCurrentTaskId } = useFlowLogStore()

  const { startExecute } = useStreamingExecuteFlow({
    socketUrl: '/v1/flow/ws_execute_flow',
    flowId: id,
    onStepStart: ({ message_type }) => {
      messageType.current = message_type
      setExecuteResult({
        ...executeResult,
        result: '',
      })
    },
    onMessage: message => {
      setExecuteResult({
        ...executeResult,
        result:
          messageType.current === StreamMessageType.PIECEWISE
            ? `${executeResult.result || ''}${message}`
            : message,
      })
    },
    onFinish: (result, hasError) => {
      const {
        status,
        task_id,
        run_result,
        message: messageFromResult,
        code,
      } = result
      let message = messageFromResult
      setCurrentTaskId(task_id)
      const isError = [
        API_CODE_CODE.COMBO_NOT_ENOUGH,
        API_CODE_CODE.COMBO_NOT_GPT4_TIME_ENOUGH,
        API_CODE_CODE.COMBO_NOT_QWENPLUS_TIME_ENOUGH,
      ].includes(code)
      stopStreamLoading()
      if (code === API_CODE_CODE.COMBO_NOT_ENOUGH) {
        message = LIMITED_ERROR_COMBO_NOT_ENOUGH
      }
      if (hasError) {
        setExecuteResult({
          status: FlowStatus.FAIL,
          taskId: task_id,
          result: null,
          errorMsg: message,
        })
        return
      }
      const computedStatus = isError ? FlowStatus.FAIL : status
      setExecuteResult({
        status: computedStatus,
        taskId: task_id,
        result: status === FlowStatus.SUCCEEDED ? run_result : null,
        stepName: message.step_name,
        errorMsg:
          computedStatus === FlowStatus.FAIL
            ? isError
              ? message
              : `运行流程节点在${message.step_name}出错, 详情请查看日志`
            : null,
      })
    },
  })

  const { runAsync: fetchExecuteLog } = useRequest(getFlowExecuteLog, {
    manual: true,
  })

  const openDebugModal = useMemoizedFn(() => {
    setOpen(true)
    setLogModalOpen(false)
  })

  const closeDebugModal = useMemoizedFn(() => {
    setOpen(false)
    const newDebugMap = { ...flowDebugMap }
    delete newDebugMap[id!]
    setFlowDebugMap(newDebugMap)
  })

  const openLogsModal = useMemoizedFn(() => {
    setLogModalOpen(true)
    closeDebugModal()
  })

  const closeLogsModal = useMemoizedFn(() => {
    setLogModalOpen(false)
  })

  const onClose = useMemoizedFn(() => {
    closeDebugModal()
    closeLogsModal()
  })

  const onInputsChange = useMemoizedFn((values: any) => {
    inputDataRef.current = values
  })

  const onRunTest = useMemoizedFn(async () => {
    const result = await validate()
    if (result) {
      deactivateNode()
      setExecuteResult({})
      setExecuteLogs([])
      setTabKey('input')
      openDebugModal()
    }
  })

  const onExecute = useMemoizedFn((values: any) => {
    if (id && versionId) {
      const params = {
        flowId: id,
        env: FlowExecuteEnv.TESTING,
        // inputData: inputDataRef.current,
        inputData: values,
        versionId,
      }
      startStreamLoading()
      setExecuteResult({
        result: null,
        errorMsg: null,
      })
      startExecute(params)
    }
  })

  const debugTask = useMemoizedFn(
    (taskId: string, extra?: Record<string, any>) => {
      setFlowDebugMap({
        ...flowDebugMap,
        [id!]: { taskId, ...extra },
      })
      setExecuteResult({})
      setExecuteLogs([])
      setTabKey('input')
      openDebugModal()
    },
  )

  const onReRun = useMemoizedFn((taskId: string, inputData: any) => {
    if (id && versionId) {
      setFlowDebugMap({
        ...flowDebugMap,
        [id!]: { taskId },
      })

      setTabKey('output')
      openDebugModal()
      const params = {
        flowId: id,
        env: FlowExecuteEnv.TESTING,
        inputData,
        versionId,
      }
      startStreamLoading()
      setExecuteResult({
        result: null,
        errorMsg: null,
      })
      startExecute(params)
    }
  })

  const fetchCurrentTaskLogs = useMemoizedFn(async (taskId?: string) => {
    if (taskId && id) {
      const res = await fetchExecuteLog({
        flowId: id,
        taskId,
      })
      if (res) {
        const { run_result } = res
        const logs = transformDataToValidLogs(run_result.actionOutputs)
        setExecuteLogs(logs)
      }
    }
  })

  useEffect(() => {
    if (!open) {
      inputDataRef.current = {}
    }
  }, [open])

  useEffect(() => {
    if (activatedNodeId) {
      closeLogsModal()
      setLogModalOpen(false)
    }
  }, [activatedNodeId])

  useEffect(() => {
    fetchCurrentTaskLogs(executeResult.taskId)
  }, [executeResult.taskId])

  return {
    flowId: id,
    debugTaskId: flowDebugMap[id!]?.taskId,
    debugTask,
    debugModalOpen: open,
    logModalOpen,
    streamLoading,
    tabKey,
    executeResult,
    executeLogs,
    openLogsModal,
    closeLogsModal,
    openDebugModal,
    onClose,
    onExecute,
    onRunTest,
    onInputsChange,
    onReRun,
  }
}
